/**
 * DV-PROJ-001: Projects API Service
 *
 * API service for Projects tab functionality following the existing
 * dashboard API patterns with cookie-based authentication.
 */

import { apiRequest } from '@/lib/api-client';
import type {
  // Project types
  ProjectsResponse,
  ProjectResponse,
  CreateProjectRequest,
  UpdateProjectRequest,

  // Character Profile types
  CharacterProfilesResponse,
  CharacterProfileResponse,
  CreateCharacterProfileRequest,
  UpdateCharacterProfileRequest,

  // Setting Profile types (Legacy)
  SettingProfilesResponse,
  SettingProfileResponse,
  CreateSettingProfileRequest,
  UpdateSettingProfileRequest,

  // Location Profile types (New)
  LocationProfilesResponse,
  LocationProfileResponse,
  CreateLocationProfileRequest,
  UpdateLocationProfileRequest,

  // Asset types
  AssetsResponse,
  AssetResponse,
  CreateAssetRequest,
  UpdateAssetRequest,

  // Creative Profile types
  CreativeProfilesResponse,
  ProjectCreativeProfilesResponse,
  UpdateProjectCreativeProfileRequest,

  // Seed generation types
  SeedGenerationJobResponse,
  SeedGenerationJobsResponse,
  CreateSeedGenerationJobRequest,

  // Upload types
  FileUploadRequest,
  FileUploadResponse,
} from '@/types/projects';

/**
 * Projects API Service
 *
 * Note: These are API contracts for backend implementation.
 * Backend endpoints need to be implemented separately.
 */
export class ProjectsAPIService {
  private static readonly BASE_PATH = '/api/projects';

  // === PROJECTS ===

  static async getProjects(params?: {
    limit?: number;
    offset?: number;
    search?: string;
  }): Promise<ProjectsResponse> {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());
    if (params?.search) searchParams.set('search', params.search);

    const query = searchParams.toString();
    const endpoint = query ? `${this.BASE_PATH}?${query}` : this.BASE_PATH;

    return apiRequest<ProjectsResponse>(endpoint);
  }

  static async getProject(projectId: string): Promise<ProjectResponse> {
    return apiRequest<ProjectResponse>(`${this.BASE_PATH}/${projectId}`);
  }

  static async createProject(data: CreateProjectRequest): Promise<ProjectResponse> {
    return apiRequest<ProjectResponse>(this.BASE_PATH, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  static async updateProject(projectId: string, data: UpdateProjectRequest): Promise<ProjectResponse> {
    return apiRequest<ProjectResponse>(`${this.BASE_PATH}/${projectId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  static async deleteProject(projectId: string): Promise<void> {
    return apiRequest<void>(`${this.BASE_PATH}/${projectId}`, {
      method: 'DELETE',
    });
  }

  static async duplicateProject(projectId: string, name: string): Promise<ProjectResponse> {
    return apiRequest<ProjectResponse>(`${this.BASE_PATH}/${projectId}/duplicate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    });
  }

  // === CHARACTER PROFILES ===

  static async getCharacterProfiles(projectId: string, params?: {
    limit?: number;
    offset?: number;
  }): Promise<CharacterProfilesResponse> {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());

    const query = searchParams.toString();
    const endpoint = query
      ? `${this.BASE_PATH}/${projectId}/characters?${query}`
      : `${this.BASE_PATH}/${projectId}/characters`;

    return apiRequest<CharacterProfilesResponse>(endpoint);
  }

  static async getCharacterProfile(projectId: string, characterId: string): Promise<CharacterProfileResponse> {
    return apiRequest<CharacterProfileResponse>(`${this.BASE_PATH}/${projectId}/characters/${characterId}`);
  }

  static async createCharacterProfile(
    projectId: string,
    data: CreateCharacterProfileRequest
  ): Promise<CharacterProfileResponse> {
    return apiRequest<CharacterProfileResponse>(`${this.BASE_PATH}/${projectId}/characters`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  static async updateCharacterProfile(
    projectId: string,
    characterId: string,
    data: UpdateCharacterProfileRequest
  ): Promise<CharacterProfileResponse> {
    return apiRequest<CharacterProfileResponse>(`${this.BASE_PATH}/${projectId}/characters/${characterId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  static async deleteCharacterProfile(projectId: string, characterId: string): Promise<void> {
    return apiRequest<void>(`${this.BASE_PATH}/${projectId}/characters/${characterId}`, {
      method: 'DELETE',
    });
  }

  // === SETTING PROFILES ===

  static async getSettingProfiles(projectId: string, params?: {
    limit?: number;
    offset?: number;
  }): Promise<SettingProfilesResponse> {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());

    const query = searchParams.toString();
    const endpoint = query
      ? `${this.BASE_PATH}/${projectId}/settings?${query}`
      : `${this.BASE_PATH}/${projectId}/settings`;

    return apiRequest<SettingProfilesResponse>(endpoint);
  }

  static async getSettingProfile(projectId: string, settingId: string): Promise<SettingProfileResponse> {
    return apiRequest<SettingProfileResponse>(`${this.BASE_PATH}/${projectId}/settings/${settingId}`);
  }

  static async createSettingProfile(
    projectId: string,
    data: CreateSettingProfileRequest
  ): Promise<SettingProfileResponse> {
    return apiRequest<SettingProfileResponse>(`${this.BASE_PATH}/${projectId}/settings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  static async updateSettingProfile(
    projectId: string,
    settingId: string,
    data: UpdateSettingProfileRequest
  ): Promise<SettingProfileResponse> {
    return apiRequest<SettingProfileResponse>(`${this.BASE_PATH}/${projectId}/settings/${settingId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  static async deleteSettingProfile(projectId: string, settingId: string): Promise<void> {
    return apiRequest<void>(`${this.BASE_PATH}/${projectId}/settings/${settingId}`, {
      method: 'DELETE',
    });
  }

  // === LOCATION PROFILES ===

  static async getLocationProfiles(projectId: string, params?: {
    limit?: number;
    offset?: number;
  }): Promise<LocationProfilesResponse> {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());

    const query = searchParams.toString();
    const endpoint = query
      ? `${this.BASE_PATH}/${projectId}/locations?${query}`
      : `${this.BASE_PATH}/${projectId}/locations`;

    return apiRequest<LocationProfilesResponse>(endpoint);
  }

  static async getLocationProfile(projectId: string, locationId: string): Promise<LocationProfileResponse> {
    return apiRequest<LocationProfileResponse>(`${this.BASE_PATH}/${projectId}/locations/${locationId}`);
  }

  static async createLocationProfile(
    projectId: string,
    data: CreateLocationProfileRequest
  ): Promise<LocationProfileResponse> {
    return apiRequest<LocationProfileResponse>(`${this.BASE_PATH}/${projectId}/locations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  static async updateLocationProfile(
    projectId: string,
    locationId: string,
    data: UpdateLocationProfileRequest
  ): Promise<LocationProfileResponse> {
    return apiRequest<LocationProfileResponse>(`${this.BASE_PATH}/${projectId}/locations/${locationId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  static async deleteLocationProfile(projectId: string, locationId: string): Promise<void> {
    return apiRequest<void>(`${this.BASE_PATH}/${projectId}/locations/${locationId}`, {
      method: 'DELETE',
    });
  }

  // === ASSETS ===

  static async getAssets(projectId: string, params?: {
    limit?: number;
    offset?: number;
    type?: string;
  }): Promise<AssetsResponse> {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());
    if (params?.type) searchParams.set('type', params.type);

    const query = searchParams.toString();
    const endpoint = query
      ? `${this.BASE_PATH}/${projectId}/assets?${query}`
      : `${this.BASE_PATH}/${projectId}/assets`;

    return apiRequest<AssetsResponse>(endpoint);
  }

  static async getAsset(projectId: string, assetId: string): Promise<AssetResponse> {
    return apiRequest<AssetResponse>(`${this.BASE_PATH}/${projectId}/assets/${assetId}`);
  }

  static async createAsset(projectId: string, data: CreateAssetRequest): Promise<AssetResponse> {
    return apiRequest<AssetResponse>(`${this.BASE_PATH}/${projectId}/assets`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  static async updateAsset(
    projectId: string,
    assetId: string,
    data: UpdateAssetRequest
  ): Promise<AssetResponse> {
    return apiRequest<AssetResponse>(`${this.BASE_PATH}/${projectId}/assets/${assetId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  static async deleteAsset(projectId: string, assetId: string): Promise<void> {
    return apiRequest<void>(`${this.BASE_PATH}/${projectId}/assets/${assetId}`, {
      method: 'DELETE',
    });
  }

  // === CREATIVE PROFILES ===

  static async getCreativeProfiles(): Promise<CreativeProfilesResponse> {
    return apiRequest<CreativeProfilesResponse>('/api/creative-profiles');
  }

  static async getProjectCreativeProfiles(projectId: string): Promise<ProjectCreativeProfilesResponse> {
    return apiRequest<ProjectCreativeProfilesResponse>(`${this.BASE_PATH}/${projectId}/creative-profiles`);
  }

  static async updateProjectCreativeProfile(
    projectId: string,
    creativeProfileId: string,
    data: UpdateProjectCreativeProfileRequest
  ): Promise<void> {
    return apiRequest<void>(`${this.BASE_PATH}/${projectId}/creative-profiles/${creativeProfileId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  // === SEED GENERATION ===

  static async createSeedGenerationJob(
    projectId: string,
    characterId: string,
    data: CreateSeedGenerationJobRequest
  ): Promise<SeedGenerationJobResponse> {
    return apiRequest<SeedGenerationJobResponse>(
      `${this.BASE_PATH}/${projectId}/characters/${characterId}/seed-jobs`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }
    );
  }

  static async getSeedGenerationJob(jobId: string): Promise<SeedGenerationJobResponse> {
    return apiRequest<SeedGenerationJobResponse>(`/api/seed-jobs/${jobId}`);
  }

  static async getSeedGenerationJobs(projectId: string, characterId?: string): Promise<SeedGenerationJobsResponse> {
    const params = new URLSearchParams();
    if (characterId) params.set('characterId', characterId);

    const query = params.toString();
    const endpoint = query
      ? `${this.BASE_PATH}/${projectId}/seed-jobs?${query}`
      : `${this.BASE_PATH}/${projectId}/seed-jobs`;

    return apiRequest<SeedGenerationJobsResponse>(endpoint);
  }

  // === FILE UPLOADS ===

  static async uploadFile(data: FileUploadRequest): Promise<FileUploadResponse> {
    const formData = new FormData();
    formData.append('file', data.file);
    if (data.projectId) formData.append('projectId', data.projectId);
    if (data.type) formData.append('type', data.type);

    return apiRequest<FileUploadResponse>('/api/upload', {
      method: 'POST',
      // Important: override default JSON headers so browser can set multipart boundary
      headers: {},
      body: formData,
      // Don't set Content-Type header - let browser set it for FormData
    });
  }

  static async getSignedUploadUrl(fileName: string, fileType: string, projectId?: string): Promise<{
    uploadUrl: string;
    fileUrl: string;
  }> {
    return apiRequest<{ uploadUrl: string; fileUrl: string }>('/api/upload/signed-url', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fileName, fileType, projectId }),
    });
  }
}